import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Gallery, Props as GalleryProps } from 'common/gallery/Gallery';
import { UploadedFileAsset } from 'common/upload/Upload';
import { Uploader, Props as UploaderProps } from 'common/upload/uploader/Uploader';

import 'common/upload/asset-uploader/AssetUploader.scss';

export interface Props {
    agencyId: number;
    tenantId: number;
    /** can be used to edit/clear media, set to [] to clear media */
    media: UploadedFileAsset[];
    uploaderOptions?: Omit<UploaderProps, 'children' | 'agencyId' | 'tenantId' | 'media' | 'noClick' | 'noDrag'>;
    /** display uploaded files, default: true */
    displayGallery?: boolean;
    galleryOptions?: Omit<GalleryProps, 'galleryData'>;
}

export const AssetUploader: FunctionComponent<Props> = ({
    agencyId,
    tenantId,
    media,
    uploaderOptions,
    displayGallery,
    galleryOptions,
    children,
}): JSX.Element => {
    const { t } = useTranslation(['common/upload']);
    const innerUploaderRef = useRef<any>(null);
    const [mediaData, setMediaData] = useState<(UploadedFileAsset | File)[]>(media! || []);

    useEffect(() => {
        setMediaData(media || []);
        if (!media?.length) {
            if (innerUploaderRef && innerUploaderRef.current) {
                innerUploaderRef.current.clearFiles();
            }
        }
    }, [media]);

    return (
        <div className="r4e-asset-uploader">
            <Uploader
                {...uploaderOptions}
                agencyId={agencyId}
                tenantId={tenantId}
                innerUploaderRef={innerUploaderRef}
                noClick
                dropzoneClassName="asset-uploader-dropzone"
            >
                <div className="click-container">
                    <i className="icon-upload" />
                    <span className="attach-text text-muted">{t('DROP_FILES')}</span>
                    <Uploader
                        {...uploaderOptions}
                        agencyId={agencyId}
                        tenantId={tenantId}
                        ref={innerUploaderRef}
                        media={mediaData}
                        noDrag
                        onFilesAccepted={(currentFiles, allFiles): void => {
                            setMediaData(allFiles);
                            if (uploaderOptions && uploaderOptions!.onFilesAccepted) {
                                uploaderOptions!.onFilesAccepted(currentFiles, allFiles);
                            }
                        }}
                    >
                        <span className="browse-text">{t('BROWSE')}</span>
                    </Uploader>
                </div>
                {children}
            </Uploader>
            {displayGallery ? (
                <Gallery
                    {...galleryOptions}
                    galleryData={mediaData}
                    onDelete={(index: number, allFiles: any): void => {
                        if (innerUploaderRef && innerUploaderRef.current) {
                            innerUploaderRef!.current!.removeFile(index);
                        }
                        if (galleryOptions && galleryOptions!.onDelete) {
                            galleryOptions!.onDelete(index, allFiles);
                        }
                    }}
                    onMediaEdited={(index: number, newFile: File): void => {
                        const newMedia = [...mediaData];

                        newMedia.splice(index + 1, 0, newFile);
                        setMediaData(newMedia);
                        if (galleryOptions && galleryOptions!.onMediaEdited) {
                            galleryOptions!.onMediaEdited(index, newFile);
                        }
                        if (innerUploaderRef && innerUploaderRef.current) {
                            innerUploaderRef.current.addAcceptedFiles([newFile]);
                        }
                    }}
                    onMediaReOrder={(files: any): void => {
                        setMediaData(files);
                        if (galleryOptions && galleryOptions!.onMediaReOrder) {
                            galleryOptions!.onMediaReOrder(files);
                        }
                    }}
                />
            ) : null}
        </div>
    );
};

AssetUploader.defaultProps = {
    displayGallery: true,
};
